import { forwardRef, useImperativeHandle, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBag, faStore } from '@fortawesome/pro-light-svg-icons';
import type { OrdersModalListEntry } from '../../models/ProductsWithoutStock';

/** Datos de la modal: tienda + lista de pedidos (refs). */
export interface OrdersWithProductModalData {
  shop: {
    name: string;
  };
  orders: OrdersModalListEntry[];
}

// 🎯 Props del modal
interface OrdersWithProductModalProps {
  onClose?: () => void;
}

// 🎯 Ref del modal
export interface OrdersWithProductModalRef {
  openModal: (orderData: OrdersWithProductModalData) => void;
  closeModal: () => void;
}

// 🎯 Modal: solo pedidos afectados (referencias), sin detalle de líneas.
export const OrdersWithProductModal = forwardRef<OrdersWithProductModalRef, OrdersWithProductModalProps>(
  ({ onClose }, ref) => {
    const { t } = useTranslation('productsWithoutStockModule');
    const [orderData, setOrderData] = useState<OrdersWithProductModalData | null>(null);
    const modalRef = useRef<ModalCenterRef>(null);

    useImperativeHandle(ref, () => ({
      openModal: (data: OrdersWithProductModalData) => {
        setOrderData(data);
        setTimeout(() => {
          modalRef.current?.openModal();
        }, 0);
      },
      closeModal: () => {
        setOrderData(null);
        modalRef.current?.closeModal();
        onClose?.();
      },
    }));

    const handleClose = () => {
      setOrderData(null);
      onClose?.();
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('modals.ordersWithProduct.title')}
        onCloseModal={handleClose}
        type="default"
        width="lg"
      >
        {orderData && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="flex flex-col gap-2 border-b border-lines pb-4 pt-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faStore} className="w-4 h-4 text-icons" />
                <span className="text-sm text-title">{orderData.shop.name}</span>
              </div>
              <div className="text-sm text-subtext">
                {t('modals.ordersWithProduct.ordersCount', { count: orderData.orders.length })}
              </div>
            </div>

            <ul className="space-y-2">
              {orderData.orders.map((order) => (
                <li
                  key={order.orderId}
                  className="flex items-center gap-3 rounded-lg border border-card-border bg-card-bg px-3 py-2.5 transition-colors"
                >
                  <FontAwesomeIcon icon={faShoppingBag} className="w-4 h-4 shrink-0 text-icons" />
                  <span className="text-sm font-medium text-title" data-testid={`modal-order-${order.orderId}`}>
                    {t('modals.ordersWithProduct.orderLabel', { ref: order.ref })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </ModalCenter>
    );
  },
);

OrdersWithProductModal.displayName = 'OrdersWithProductModal';
